"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
const tooltipPopupVariants = cva(["bg-primary text-primary-foreground", "w-fit max-w-72 origin-(--transform-origin) rounded-xl px-3 py-1.5", "text-xs text-balance", "transition-[transform,opacity] data-[instant]:transition-none", "data-[starting-style]:opacity-0 data-[starting-style]:scale-95", "data-[ending-style]:opacity-0 data-[ending-style]:scale-95"]);
function TooltipProvider(t0) {
  const $ = _c(6);
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      delay: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = t1;
  } else {
    props = $[1];
    t1 = $[2];
  }
  const delay = t1 === undefined ? 0 : t1;
  let t2;
  if ($[3] !== delay || $[4] !== props) {
    t2 = <TooltipPrimitive.Provider data-slot="tooltip-provider" delay={delay} {...props} />;
    $[3] = delay;
    $[4] = props;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function Tooltip(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <TooltipPrimitive.Root data-slot="tooltip" {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
type TooltipTriggerProps = React.ComponentProps<typeof TooltipPrimitive.Trigger>;
function TooltipTrigger(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function TooltipPortal(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <TooltipPrimitive.Portal data-slot="tooltip-portal" {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function TooltipPositioner(t0) {
  const $ = _c(10);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      sideOffset: t1,
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const sideOffset = t1 === undefined ? 4 : t1;
  let t2;
  if ($[4] !== className) {
    t2 = cn("isolate z-50", className);
    $[4] = className;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== sideOffset || $[8] !== t2) {
    t3 = <TooltipPrimitive.Positioner data-slot="tooltip-positioner" sideOffset={sideOffset} className={t2} {...props} />;
    $[6] = props;
    $[7] = sideOffset;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
function TooltipPopup(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(tooltipPopupVariants(), className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <TooltipPrimitive.Popup data-slot="tooltip-popup" role="tooltip" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
const tooltipArrowVariants = cva(["bg-primary fill-primary", "z-50 size-2.5 rotate-45 rounded-[2px]", "data-[side=top]:-bottom-0.5 data-[side=bottom]:-top-0.5", "data-[side=left]:-right-0.5 data-[side=right]:-left-0.5", "data-[side=inline-start]:-end-0.5 data-[side=inline-end]:-start-0.5"]);
function TooltipArrow(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(tooltipArrowVariants(), className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <TooltipPrimitive.Arrow data-slot="tooltip-arrow" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
type TooltipContentProps = React.ComponentProps<typeof TooltipPrimitive.Popup> & Pick<React.ComponentProps<typeof TooltipPrimitive.Positioner>, "side" | "align" | "sideOffset">;
function TooltipContent(t0) {
  const $ = _c(17);
  let align;
  let children;
  let className;
  let props;
  let side;
  let sideOffset;
  if ($[0] !== t0) {
    ({
      className,
      sideOffset,
      side,
      align,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = align;
    $[2] = children;
    $[3] = className;
    $[4] = props;
    $[5] = side;
    $[6] = sideOffset;
  } else {
    align = $[1];
    children = $[2];
    className = $[3];
    props = $[4];
    side = $[5];
    sideOffset = $[6];
  }
  let t1;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <TooltipArrow />;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== children || $[9] !== className || $[10] !== props) {
    t2 = <TooltipPopup className={className} {...props}>{children}{t1}</TooltipPopup>;
    $[8] = children;
    $[9] = className;
    $[10] = props;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== align || $[13] !== side || $[14] !== sideOffset || $[15] !== t2) {
    t3 = <TooltipPortal><TooltipPositioner side={side} align={align} sideOffset={sideOffset}>{t2}</TooltipPositioner></TooltipPortal>;
    $[12] = align;
    $[13] = side;
    $[14] = sideOffset;
    $[15] = t2;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
}
export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TooltipPortal, TooltipPositioner, TooltipPopup, TooltipArrow, tooltipPopupVariants, tooltipArrowVariants };